/**
 * conso-video-som
 *
 * A galeria do Horizon renderiza vídeo de produto sempre `autoplay muted` e sem
 * controles — `snippets/product-media.liquid` passa `disable_controls: true` fixo
 * e `snippets/video.liquid` crava `autoplay: true, muted: true` no `video_tag`.
 * Sem controles o visitante não tem como ativar o som.
 *
 * Este script é aditivo: não toca nenhum arquivo do Horizon, só pendura um botão
 * de alto-falante sobre o vídeo da galeria. Autoplay com som é bloqueado por todo
 * browser, então o vídeo continua começando mudo — o clique é o gesto que libera.
 *
 * O <video> nasce dentro de um <template> do <deferred-media> e só entra no DOM
 * quando o custom element hidrata, por isso o MutationObserver.
 */
(function () {
  'use strict';

  // O asset agora é carregado em dois lugares: pela section da LP
  // (conso-pg-produto-lp) e pelo bloco da galeria (_product-media-gallery), que
  // cobre a PDP. Numa LP os dois disparam — sem esta trava o boot rodaria duas
  // vezes e penduraria dois MutationObserver no body.
  if (window.__consoVideoSomBooted) return;
  window.__consoVideoSomBooted = true;

  // `media-gallery` entra no escopo pra a PDP funcionar sem precisar de um
  // wrapper próprio: o bloco da galeria tem `tag: null`, então não há elemento
  // dele onde pendurar o data-attribute. O seletor antigo continua valendo pras
  // sections da LP, que marcam o <featured-product-information>.
  var SCOPE = '[data-conso-video-som], media-gallery';
  var MARK = 'consoVideoSom';

  var ICON_MUTED =
    '<svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true">' +
    '<path d="M4 7.5h2.5L10 4.5v11L6.5 12.5H4z" fill="currentColor"/>' +
    '<path d="M13 8l4 4M17 8l-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
    '</svg>';

  var ICON_UNMUTED =
    '<svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true">' +
    '<path d="M4 7.5h2.5L10 4.5v11L6.5 12.5H4z" fill="currentColor"/>' +
    '<path d="M12.8 7.6a3.2 3.2 0 010 4.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
    '<path d="M15.1 5.6a6 6 0 010 8.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
    '</svg>';

  /** Todos os vídeos já preparados nesta página. */
  function allVideos() {
    return Array.prototype.slice.call(document.querySelectorAll(SCOPE + ' video[data-conso-som-on]'));
  }

  function paint(button, video) {
    var on = !video.muted;
    button.innerHTML = on ? ICON_UNMUTED : ICON_MUTED;
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    button.setAttribute('aria-label', on ? 'Desativar som do vídeo' : 'Ativar som do vídeo');
    button.classList.toggle('conso-som--on', on);
  }

  function enhance(video) {
    if (video.dataset[MARK]) return;

    /*
     * Host = .product-media-container, que o tema já cria com position:relative
     * (assets/base.css:1932 e o equivalente do dialog de zoom). NÃO pendurar em
     * <deferred-media>: base.css:2245 estica todo filho direto depois do
     * poster-button pra position:absolute/top:0/width:100%, e o botão viraria um
     * overlay do tamanho do vídeo.
     */
    var host = video.closest('.product-media-container');
    if (!host || host.querySelector(':scope > .conso-som')) return;

    video.dataset[MARK] = '1';
    video.setAttribute('data-conso-som-on', '');

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'conso-som';

    /*
     * O .product-media-container carrega on:click="#zoom-dialog-.../open/N" e o
     * Horizon delega esses atributos no document com { capture: true }
     * (assets/component.js:224-278) — capture roda antes do nosso handler, então
     * stopPropagation() sozinho não impede o zoom de abrir.
     *
     * getElement() (component.js:282) só sobe com closest('[on\\:click]') quando o
     * próprio alvo NÃO tem o atributo. Dando um on:click ao botão, a busca para
     * aqui e nunca alcança o slide. O valor não resolve pra método nenhum de
     * Component, então o Horizon segue direto sem executar nada.
     */
    button.setAttribute('on:click', '/consoSomNoop');

    paint(button, video);

    button.addEventListener('click', function (event) {
      // O <deferred-media> delega clique pra /toggleMedia e pausaria o vídeo.
      event.preventDefault();
      event.stopPropagation();

      var ligando = video.muted;

      if (ligando) {
        // Só um vídeo com som por vez — a LP tem três kits na mesma página.
        allVideos().forEach(function (outro) {
          if (outro === video) return;
          outro.muted = true;
          var c = outro.closest('.product-media-container');
          var b = c && c.querySelector(':scope > .conso-som');
          if (b) paint(b, outro);
        });
      }

      video.muted = !ligando;
      if (video.paused) {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
      paint(button, video);
    });

    // Se algo no tema remutar o vídeo, o ícone acompanha.
    video.addEventListener('volumechange', function () {
      paint(button, video);
    });

    host.appendChild(button);
  }

  function scan(root) {
    var scopes = (root || document).querySelectorAll ? (root || document).querySelectorAll(SCOPE) : [];
    Array.prototype.forEach.call(scopes, function (scope) {
      Array.prototype.forEach.call(scope.querySelectorAll('video'), enhance);
    });
    // root pode ser o próprio vídeo injetado dentro de um escopo já existente.
    if (root && root.matches && root.matches('video') && root.closest(SCOPE)) enhance(root);
  }

  function boot() {
    scan(document);

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('video')) {
            if (node.closest(SCOPE)) enhance(node);
            return;
          }
          if (node.querySelector && node.querySelector('video')) scan(node);
        });
      });
    });

    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
